import React, { ReactNode, useState, useCallback } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import ReminderModel, {
  ReminderView,
  ReminderData,
} from "../../core/api/reminder/ReminderModel";
import { confirmDialog } from "../common/ConfirmDialog";
import { Delete, Edit, Pageview } from "@mui/icons-material";
import { useNotifications } from "../../hooks/NotificationProvider";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import qs from "qs";
import ReminderModalForm from "./ReminderModalForm";
import { IconButton, Box, Tooltip } from "@mui/material";
import { translations } from "../../translations/common";
import { tableContainerStyles, dataGridStyles } from "../common/styles/TableStyles";
import { PersistentSearchHeader } from "../PersistentSearchHeader";
import { GoToPagination } from "components/GoToPagination";

interface Props {
  customHeader?: ReactNode;
  refresh(): void;
  isLoading: boolean;
  pageSize?: number;
  reminders: ReminderView[];
  totalCount: number;
}

const ReminderTable: React.FC<Props> = ({
  customHeader,
  reminders,
  refresh,
  isLoading,
  totalCount,
}) => {
  const [reminder, setReminder] = useState<ReminderView | null>(null);
  const notifications = useNotifications();
  const navigate = useNavigate();

  // Ensure each row has an id for DataGrid
  const rowsWithIds = reminders.map(reminder => ({
    ...reminder,
    id: reminder.clientId || `reminder-${reminder.companyTitle}`
  }));

  const handleView = useCallback((id: string) => {
    navigate({
      pathname: `/client/${id}`,
      search: qs.stringify({ tab: "reminders" }),
    });
  }, [navigate]);

  const handleEdit = useCallback((reminder: ReminderView) => {
    setReminder(reminder);
  }, []);

  const handleDelete = useCallback((reminder: ReminderView) => {
    confirmDialog({
      title: translations.reminders.table.confirmations.deleteTitle,
      body: translations.reminders.table.confirmations.deleteBody(reminder.companyTitle),
      action: async () => {
        try {
          await ReminderModel.delete(reminder.clientId);
          refresh();
          notifications.notify({
            level: "success",
            message: translations.reminders.table.notifications.deleteSuccess,
          });
        } catch (err: any) {
          notifications.notify({
            level: "error",
            message: err.response?.data?.description || translations.reminders.table.notifications.deleteError,
          });
        }
      },
      actionName: translations.reminders.table.confirmations.deleteAction,
    });
  }, [notifications, refresh]);

  const renderActions = useCallback((params: GridRenderCellParams<ReminderView>) => {
    return (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title={translations.reminders.table.actions.view}>
          <IconButton
            onClick={() => handleView(params.row.clientId)}
            size="small"
          >
            <Pageview />
          </IconButton>
        </Tooltip>
        <Tooltip title={translations.reminders.table.actions.edit}>
          <IconButton
            onClick={() => handleEdit(params.row)}
            size="small"
          >
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title={translations.reminders.table.actions.delete}>
          <IconButton
            onClick={() => handleDelete(params.row)}
            size="small"
            color="error"
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [handleView, handleEdit, handleDelete]);

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: translations.reminders.table.columns.date,
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Box>
          {format(new Date(params.row.date), "yyyy-MM-dd HH:mm")}
        </Box>
      ),
    },
    {
      field: "companyTitle",
      headerName: translations.reminders.table.columns.companyTitle,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: '1.2',
          padding: '8px 0'
        }}>
          {params.row.companyTitle}
        </Box>
      ),
    },
    {
      field: "clientId",
      headerName: translations.reminders.table.columns.clientId,
      flex: 1,
      renderCell: (params) => (
        <Box>
          {params.row.clientId}
        </Box>
      ),
    },
    {
      field: "clientState",
      headerName: translations.reminders.table.columns.clientState,
      width: 150,
      renderCell: (params) => (
        <Box>
          {translations.reminders.clientStates[params.row.clientState as keyof typeof translations.reminders.clientStates] || params.row.clientState}
        </Box>
      ),
    },
    {
      field: "comment",
      headerName: translations.reminders.table.columns.comment,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{
          whiteSpace: 'pre-wrap', // Allows wrapping + respects line breaks
          wordBreak: 'break-word',
          textAlign: 'left',
          width: '100%',
          height: '100%',
        }}>
          {params.row.comment}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 150,
      sortable: false,
      renderCell: renderActions,
    },
  ];

  const renderTableHeader = () => {
    if (customHeader) {
      return customHeader;
    }

    return (
      <PersistentSearchHeader
        title="Priminimai"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => setReminder(ReminderModel.defaultValue() as ReminderView)}
              size="small"
            >
              <Edit />
            </IconButton>
          </Box>
        }
      />
    );
  };

  return (
    <Box sx={{ ...tableContainerStyles }}>
      {renderTableHeader()}
      <DataGrid
        rows={rowsWithIds}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        loading={isLoading}
        paginationMode="server"
        pageSizeOptions={[10]}
        rowCount={totalCount}
        getRowHeight= {() => "auto"}

        slots={{
          pagination: () => (
            <GoToPagination
              count={totalCount}
              rowsPerPageOptions={[10]}
              pageSize={10}
            />
          ),
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {translations.reminders.table.noData}
            </Box>
          ),
        }}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        sx={{
          ...dataGridStyles,
          maxHeight: '80vh',
        }}
      />
      {reminder && (
        <ReminderModalForm
          value={reminder}
          save={async (values: ReminderData) => {
            const resp = await ReminderModel.updateOrCreate(
              reminder.clientId,
              values
            );
            refresh();
            return resp;
          }}
          close={() => setReminder(null)}
        />
      )}
    </Box>
  );
};

export default ReminderTable;
